import type { Phase } from '@alpaca-software/40kdc-data';
import * as z from 'zod';
import type { HordePhase } from './game-state';
import { GAME_PHASES } from './phase.util';

/**
 * Content schemas (contracts §5).
 *
 * The spawn-bracket tables and manifest are served from `apps/horde-web/public/content/` and parsed through these
 * schemas by the generated data-access services. Faction datasheets come directly from the embedded 40kdc dataset.
 * The rules, faction rules, decks, and
 * Resupply Table are no longer fetched at all - they are compiled into the bundle as `@animus/type/game-data`, and these
 * schemas type that data and guard it in `game-data`'s spec.
 */

// --- Spawn brackets ---
export const SpawnCellSchema = z.object({
    none: z.boolean(),
    raw: z.string(),
    units: z.array(
        z.object({
            count: z.number(),
            /** Canonical Horde sources link concrete 40k units by this immutable upstream id. */
            datasheetId: z.string().optional(),
            /**
             * Horde faction namespace for `datasheetId`. 40kdc ids are only unique within a faction bundle, so
             * persisted/runtime ids use this namespace to distinguish otherwise colliding upstream slugs.
             */
            factionId: z.string().optional(),
            /** Transitional display label retained until persisted/session data moves to ID-only references. */
            name: z.string(),
        })
    ),
});
export type SpawnCell = z.infer<typeof SpawnCellSchema>;

/** Separator used by the canonical, faction-qualified 40k datasheet reference. */
export const HORDE_DATASHEET_ID_SEPARATOR = '::';

/**
 * 40kdc's `id` is faction-scoped (for example, Grey Knights and Space Marines both have a
 * `venerable-dreadnought`). Every Horde-facing data boundary therefore uses this stable qualified key instead.
 */
export function qualifyHordeDatasheetId(factionId: string, datasheetId: string): string {
    return `${factionId}${HORDE_DATASHEET_ID_SEPARATOR}${datasheetId}`;
}

export const SpawnBracketSchema = z.enum(['3-4', '5-6', '7-9', '10+']);
export type SpawnBracket = z.infer<typeof SpawnBracketSchema>;

export const FactionSpawnDataSchema = z.object({
    brackets: z.array(SpawnBracketSchema),
    factionId: z.string(),
    name: z.string().nullable(),
    rows: z.array(
        z.object({
            entries: z.record(z.string(), SpawnCellSchema),
            roll: z.number(),
        })
    ),
});
export type FactionSpawnData = z.infer<typeof FactionSpawnDataSchema>;

/**
 * `/content/spawn-brackets/_index.json` - the canonical faction list.
 */
export const SpawnIndexSchema = z.object({
    factions: z.array(z.object({ id: z.string(), name: z.string() })),
    /** Runtime filenames are content-addressed; checked-in Horde source indexes omit this generated field. */
    files: z.record(z.string(), z.string()).default({}),
});
export type SpawnIndex = z.infer<typeof SpawnIndexSchema>;

// --- Decks ---
export const DeckCardDeckSchema = z.enum(['misery', 'secondary', 'secret-objective', 'resupply']);
export type DeckCardDeck = z.infer<typeof DeckCardDeckSchema>;

export const DeckCardSchema = z.object({
    deck: DeckCardDeckSchema,
    flavor: z.string().optional(),
    id: z.string(),
    images: z.object({ printer: z.string(), vanity: z.string() }),
    name: z.string(),
    punishment: z.string().optional(),
    reward: z.string().optional(),
    tags: z.array(z.string()).optional(),
    text: z.string(),
});
export type DeckCard = z.infer<typeof DeckCardSchema>;

export const DeckCardArraySchema = z.array(DeckCardSchema);

// --- Deployment maps ---
export const DeploymentMapSchema = z.object({
    id: z.string(),
    image: z.string(),
    name: z.string(),
    recommended: z.boolean(),
    sourceSha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export type DeploymentMap = z.infer<typeof DeploymentMapSchema>;

export const DeploymentMapArraySchema = z.array(DeploymentMapSchema);

// --- Resupply ---
export const ResupplyEntrySchema = z.object({
    cost: z.union([z.number(), z.literal('X')]),
    description: z.string(),
    id: z.string(),
    name: z.string(),
    tags: z.array(z.string()).optional(),
});
export type ResupplyEntry = z.infer<typeof ResupplyEntrySchema>;

export const ResupplyTableSchema = z.array(ResupplyEntrySchema);

// --- Rules ---
export const RuleSectionSchema = z.object({
    body: z.string(),
    id: z.string(),
    number: z.string(),
    order: z.number(),
    parentId: z.string().nullable(),
    title: z.string(),
});
export type RuleSection = z.infer<typeof RuleSectionSchema>;

export const RuleSectionArraySchema = z.array(RuleSectionSchema);

// --- Faction rules (§7.5) ---
export const FactionRuleEffectKindSchema = z.enum([
    'displayOnly',
    'spawnModifier',
    'moveStatMod',
    'startOfPhaseMortalWounds',
    'perActivationRoll',
    'postMoveEligibility',
    'respawnOnDeath',
    'persistentEnemyMark',
    'extraMiseryReveal',
]);
export type FactionRuleEffectKind = z.infer<typeof FactionRuleEffectKindSchema>;

export const FactionRuleSchema = z.object({
    effectKind: FactionRuleEffectKindSchema,
    effectParams: z.record(z.string(), z.unknown()).optional(),
    factionId: z.string(),
    factionName: z.string(),
    id: z.string(),
    name: z.string(),
    text: z.string(),
});
export type FactionRule = z.infer<typeof FactionRuleSchema>;

export const FactionRuleArraySchema = z.array(FactionRuleSchema);

/**
 * Maps a faction id to the id its Primary Faction Rule (§7.5) is published under. Space Marine chapters (e.g.
 * `space-marines-space-wolves`) have no chapter-specific rule and share the generic `space-marines` one.
 */
export function canonicalFactionRuleId(factionId: string): string {
    return factionId.startsWith('space-marines-') ? 'space-marines' : factionId;
}

export const ABILITY_PHASE_TO_HORDE_PHASE = {
    charge: 'horde-charge',
    command: 'horde-command',
    fight: 'horde-fight',
    movement: 'horde-movement',
    shooting: 'horde-shooting',
} as const satisfies Record<Phase, HordePhase>;
// --- Manifest ---

/**
 * Shape of `HORDE_CONTENT_MANIFEST` (`@animus/type/game-data`), the compiled-in identity of the Horde content a build
 * serves. Every field is build-time knowledge, so this is never parsed at runtime - the schema is here to keep the
 * shape declared alongside the rest of the content types and to be the source of the type.
 */
export const ContentManifestSchema = z.object({
    contentHash: z.string().length(64),
    decks: z.record(z.string(), z.number()),
    documents: z.array(
        z.object({
            affectsSession: z.boolean(),
            byteSize: z.number().int().nonnegative(),
            id: z.string(),
            logicalId: z.string(),
            schemaVersion: z.number().int().positive(),
            sha256: z.string().length(64),
            source: z.object({
                integrity: z.string(),
                name: z.string(),
                version: z.string(),
            }),
            url: z.string(),
        })
    ),
    factions: z.array(
        z.object({
            id: z.string(),
            name: z.string(),
        })
    ),
    generatorVersion: z.string(),
    rulesSections: z.number(),
    schemaVersion: z.number().int().positive(),
    sources: z.array(
        z.object({
            affectsSession: z.boolean(),
            byteSize: z.number().int().nonnegative(),
            id: z.string(),
            logicalId: z.string(),
            schemaVersion: z.number().int().positive(),
            sha256: z.string().length(64),
            source: z.object({
                integrity: z.string(),
                name: z.string(),
                version: z.string(),
            }),
            url: z.string(),
        })
    ),
});
export type ContentManifest = z.infer<typeof ContentManifestSchema>;

// --- Keyword index (linked keywords with rich definition popovers) ---
/**
 * Category of a keyword-index entry. Drives the popover styling (kind badge) and which source authored the
 * definition: base-40K glossary entries are the most root/accurate, 40kdc abilities are structured describe()
 * prose, horde glossary entries are app-authored Horde terms, weapon-keyword entries come from the 40kdc catalog
 * (+ hand-authored blurbs).
 */
export const KeywordIndexKindSchema = z.enum(['base-40k', 'ability', 'horde', 'weapon-keyword']);
export type KeywordIndexKind = z.infer<typeof KeywordIndexKindSchema>;

/**
 * One entry in the keyword index. `id` is stable; `term` is the canonical display term; `aliases` are alternate
 * casings/forms that resolve to this entry. A parameterized ability (Feel No Pain 5+, Sustained Hits 1/D3) has its
 * root term indexed once with the definition, and every used instance emitted as its own entry with `params`
 * carrying the instantiated value(s) - the link shows the full parameterized text while the popover shows the
 * root definition.
 */
export const KeywordIndexEntrySchema = z.object({
    aliases: z.array(z.string()),
    definition: z.string(),
    id: z.string(),
    kind: KeywordIndexKindSchema,
    /** Instantiated parameter values when this entry is a parameterized instance (e.g. ['5+'] for 'Feel No Pain 5+'). */
    params: z.array(z.string()).optional(),
    phases: z.array(z.enum(GAME_PHASES)).optional(),
    /** rules.json section id this term deep-links to (horde terms always have one; base-40K terms never do). */
    sectionId: z.string().optional(),
    /** Provenance: which source authored the definition. */
    source: z.string(),
    term: z.string(),
});
export type KeywordIndexEntry = z.infer<typeof KeywordIndexEntrySchema>;

export const KeywordIndexSchema = z.object({
    /** Content-hashed filename of this index document (single document, unlike per-faction bundles). */
    entries: z.array(KeywordIndexEntrySchema),
    schemaVersion: z.number().int().positive(),
});
export type KeywordIndex = z.infer<typeof KeywordIndexSchema>;

/** `public/content/keywords/_index.json` - points at the single content-addressed index document. */
export const KeywordIndexPointerSchema = z.object({
    file: z.string(),
});
export type KeywordIndexPointer = z.infer<typeof KeywordIndexPointerSchema>;
